/**
 * Session/local storage helpers for the sponsor popup (see PromoPopup.tsx and
 * useSponsorPopupSchedule.ts). Every storage access is wrapped in try/catch:
 * Safari private mode and storage blocked by policy must never break
 * rendering, and the once-per-session rule still holds via the in-memory
 * fallback below when storage itself throws.
 */

// Set once the popup has been shown this browser session, so it never shows
// a second time even if the visitor keeps browsing well past the delay.
const SESSION_FLAG_KEY = 'tcg-sponsor-popup-shown';

// Holds a single JSON value, { "until": <epoch ms> }, written whenever the
// visitor dismisses the popup. Different dismissal paths write different
// windows; see SUPPRESSION_DAYS below.
const SUPPRESSION_KEY = 'tcg-sponsor-popup';

// Retired keys from the popup's earlier 15-second-timer implementation
// (removed from the app shell in PR #33). Their 7-day suppression windows
// expired months ago; clean them up whenever suppression state is read.
const LEGACY_DISMISSED_KEY = 'promoPopupDismissed';
const LEGACY_DISMISSED_TIME_KEY = 'promoPopupDismissedTime';

export const SUPPRESSION_DAYS = {
  close: 7,
  sponsorClick: 30,
  optOut: 365,
} as const;

// In-memory fallback so "at most once per session" still holds when
// sessionStorage throws.
let sessionShownFallback = false;

export const hasShownThisSession = (): boolean => {
  try {
    if (window.sessionStorage.getItem(SESSION_FLAG_KEY) === '1') return true;
  } catch {
    // sessionStorage unavailable; fall through to the in-memory flag below.
  }
  return sessionShownFallback;
};

export const markShownThisSession = (): void => {
  sessionShownFallback = true;
  try {
    window.sessionStorage.setItem(SESSION_FLAG_KEY, '1');
  } catch {
    // The in-memory flag above already keeps this session's once-only promise.
  }
};

// getSuppressionActive() is called on every deferred re-check while the
// popup waits for a clear moment, which can be many times a session; the
// legacy keys only ever need removing once per page load.
let legacyKeysCleared = false;

const removeLegacyKeys = (): void => {
  if (legacyKeysCleared) return;
  legacyKeysCleared = true;
  try {
    window.localStorage.removeItem(LEGACY_DISMISSED_KEY);
    window.localStorage.removeItem(LEGACY_DISMISSED_TIME_KEY);
  } catch {
    // Nothing to clean up if storage is unavailable.
  }
};

const readSuppressionUntil = (): number => {
  removeLegacyKeys();
  try {
    const raw = window.localStorage.getItem(SUPPRESSION_KEY);
    if (!raw) return 0;
    const parsed = JSON.parse(raw) as { until?: unknown };
    return typeof parsed.until === 'number' ? parsed.until : 0;
  } catch {
    return 0;
  }
};

export const getSuppressionActive = (): boolean => Date.now() < readSuppressionUntil();

export const suppressSponsorPopupFor = (days: number): void => {
  try {
    const until = Date.now() + days * 24 * 60 * 60 * 1000;
    window.localStorage.setItem(SUPPRESSION_KEY, JSON.stringify({ until }));
  } catch {
    // Storage denied (Safari private mode, policy). The in-session flag
    // above still stops a repeat this browser session, which is the best
    // that can be done without storage.
  }
};

/**
 * Test-only: clears the in-memory once-per-session fallback and the
 * once-per-page-load legacy-key-cleanup flag between tests.
 */
export const resetSponsorPopupSessionForTests = (): void => {
  sessionShownFallback = false;
  legacyKeysCleared = false;
};
